import { useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import SuiBox from "components/sui/SuiBox";
import SuiTypography from "components/sui/SuiTypography";
import SuiButton from "components/sui/SuiButton";

import CdxInput from "components/cdx/CdxInput";
import CdxPasswordInput from "components/cdx/CdxPasswordInput";

import { Form, Field } from "react-final-form";
import { FORM_ERROR } from "final-form";
import signInWithEmailLink from "app/fb/signInWithEmailLink";

import { auth } from "app/fb/config";
import reloadToken from "app/fb/reloadToken";
import { deploySnackbar } from "actions";
import { updatePassword } from "firebase/auth";

import { LSK_EMAIL_FOR_RESET_PASSWORD } from "settings/localStorageKeys";

const CompleteSignUpForm = ({ deploySnackbar }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState({});

  const validateForm = (values) => {
    const error = {};

    if (!values.email) {
      error.email = "Please enter email";
    } else {
      const validateEmail = (email) => {
        var re = /\S+@\S+\.\S+/;
        return re.test(email);
      };
      if (!validateEmail(values.email)) {
        error.email = "Please enter a valid email";
      }
    }

    if (values.password) {
      if (values.password.length < 8) {
        error.password = "At least 8 character";
      } else if (values.password.search(/[A-Z]/) < 0) {
        error.password =
          "Your password must be contained at least one upper case letter";
      } else if (values.password.search(/[a-z]/) < 0) {
        error.password =
          "Your password must be contained at least one lower case letter";
      } else if (values.password.search(/\d/) < 0) {
        error.password = "Your password must be contained at least one number";
      } else if (values.password.search(/[!@#$%^&*()+-/.]/) < 0) {
        error.password =
          "Your password must be contained at least one special character";
      }
    } else {
      error.password = "Please enter a password";
    }
    if (values.re_password) {
      if (values.re_password !== values.password) {
        error.re_password = "Password do not match";
      }
    } else {
      error.re_password = "Please confirm a password";
    }

    return error;
  };

  const onSubmit = async (values) => {
    setMessage({ color: "dark", text: "Changing your password..." });
    const error = {};
    const result = await signInWithEmailLink(values.email, values.password);
    if (result.type === "success") {
    } else if (result.type === "error") {
      if (result.data.code === "auth/invalid-email") {
        error.email = "Invalid email";
      } else if (result.data.code === "auth/invalid-action-code") {
        error[FORM_ERROR] =
          "Invalid sign up code, please make another sign up request.";
      } else {
        error[FORM_ERROR] = "Unable to sign up, please try again";
      }
      return error;
    }

    try {
      const user = auth.currentUser;
      await updatePassword(user, values.password);
      const snackBar = {
        color: "success",
        icon: "done",
        title: "Successfully change password",
        content: `${t("Your password has been changed.")}`,
      };
      deploySnackbar(snackBar);
      setMessage({});
      setTimeout(async () => {
        reloadToken();
      }, 500);
      window.localStorage.removeItem(LSK_EMAIL_FOR_RESET_PASSWORD);
    } catch (error) {
      setMessage({});
      return { [FORM_ERROR]: "Unknown error. Please try again later." };
    }

    return error;
  };

  return (
    <Form
      onSubmit={onSubmit}
      validate={validateForm}
      render={({ handleSubmit, form, submitError, submitting, pristine }) => {
        const formState = form.getState();
        return (
          <SuiBox
            component="form"
            role="form"
            width="100%"
            onSubmit={handleSubmit}
          >
            <Field
              name="email"
              initialValue={window.localStorage.getItem(
                LSK_EMAIL_FOR_RESET_PASSWORD
              )}
            >
              {({ input, meta }) => (
                <CdxInput
                  name={input.name}
                  value={input.value}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  mb={1}
                  label={t("Email")}
                  error={
                    !!(meta.error && meta.touched) ||
                    !!meta.submitError ||
                    !!submitError
                  }
                  message={
                    ((meta.error && meta.touched) || meta.submitError) &&
                    (t(meta.error) || t(meta.submitError))
                  }
                />
              )}
            </Field>
            <Field name="password" type="password">
              {({ input, meta }) => (
                <CdxPasswordInput
                  name={input.name}
                  type={input.type}
                  value={input.value}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  mb={1}
                  label={t("Password*")}
                  error={!!(meta.error && meta.touched) || !!meta.submitError}
                  message={
                    ((meta.error && meta.touched) || meta.submitError) &&
                    (t(meta.error) || t(meta.submitError))
                  }
                />
              )}
            </Field>
            <Field name="re_password" type="password">
              {({ input, meta }) => (
                <CdxPasswordInput
                  name={input.name}
                  type={input.type}
                  value={input.value}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  mb={1}
                  label={t("Confirm password*")}
                  error={!!(meta.error && meta.touched) || !!meta.submitError}
                  message={
                    ((meta.error && meta.touched) || meta.submitError) &&
                    (t(meta.error) || t(meta.submitError))
                  }
                />
              )}
            </Field>
            {submitError && (
              <SuiBox>
                <SuiTypography color="error" variant="caption">
                  {t(submitError)}
                </SuiTypography>
              </SuiBox>
            )}
            {message.text && (
              <SuiBox>
                <SuiTypography
                  color={message.color || "info"}
                  variant="caption"
                >
                  {t(message.text)}
                </SuiTypography>
              </SuiBox>
            )}
            <SuiBox mt={3}>
              <SuiButton
                variant="gradient"
                color="info"
                type="submit"
                fullWidth
                disabled={
                  submitting || Object.values(formState.errors).length > 0
                }
              >
                {t("Reset password")}
              </SuiButton>
            </SuiBox>
          </SuiBox>
        );
      }}
    />
  );
};

const mapStateToProps = () => {
  return {};
};

const mapDispatchToProps = { deploySnackbar };

export default connect(mapStateToProps, mapDispatchToProps)(CompleteSignUpForm);
